import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/**
 * Retrieves data from PrimeReact p-datatable.
 * @returns An array of objects representing table rows.
 */
const getGridData = () => {
    const headers = document.querySelectorAll(".p-datatable-thead th");
    const headerTitles = Array.from(headers).map(
        header => header.textContent?.trim() || ""
    );

    // Find indexes of headers we want to exclude
    const excludeHeaders = ["Actions"];
    const excludeIndexes = headerTitles
        .map((title, i) => (excludeHeaders.includes(title) ? i : -1))
        .filter(i => i !== -1);

    const filteredHeaderTitles = headerTitles.filter(
        (_, i) => !excludeIndexes.includes(i)
    );

    const rows = document.querySelectorAll(".p-datatable-tbody tr");

    const data = Array.from(rows).map((row, rowIndex) => {
        const cells = row.querySelectorAll("td");

        const entries = Array.from(cells)
            .map<[string, string]>((cell, cellIndex) => {
                const canvas = cell.querySelector("canvas");
                let cellValue: string;

                if (canvas) {
                    cellValue = canvas.toDataURL();
                } else {
                    cellValue = cell.textContent?.trim() || "";
                }

                return [headerTitles[cellIndex], cellValue];
            })
            .filter(([key], idx) => !excludeIndexes.includes(idx));

        return {
            "No.": rowIndex + 1,
            ...Object.fromEntries(entries),
        };
    });

    return { headerTitles: filteredHeaderTitles, data };
};



/**
 * Exports table data to an Excel file.
 * @param fileName - The name of the exported file.
 */
export const exportExcel = (fileName: string = "export") => {
    const { headerTitles, data } = getGridData();

    // Convert data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

/**
 * Exports table data to a PDF file.
 * @param fileName - The name of the exported file.
 */
export const exportPDF = (fileName: string = "export") => {
    const { headerTitles, data } = getGridData();

    const doc = new jsPDF();
    doc.text(fileName, 14, 10);

    const bodyData = data.map((row) => Object.values(row));

    (doc as any).autoTable({
        head: [headerTitles],
        body: bodyData,
    });

    // Save the PDF
    doc.save(`${fileName}.pdf`);
};

export const exportQrPDF = (fileName: string = "export") => {
    const { headerTitles, data } = getGridData();

    const doc = new jsPDF();
    doc.text(fileName, 14, 10);

    // Prepare bodyData and store QR images in a map
    const qrImageMap = new Map(); // key: rowIndex-columnIndex, value: image data

    const bodyData = data.map((row, rowIndex) => {
        return headerTitles.map((header, columnIndex) => {
            const value = (row as Record<string, any>)[header];

            if (header === "QR" && typeof value === "string" && value.startsWith('data:image')) {
                // Save QR image in map using row and column as key
                qrImageMap.set(`${rowIndex}-${columnIndex}`, value);
                return ''; // Show empty text in the table
            }

            return value; // Show normal text
        });
    });

    (doc as any).autoTable({
        head: [headerTitles],
        body: bodyData,
        styles: {
            cellPadding: 10, // Make rows taller
        },
        didDrawCell: function (data: any) {
            const columnIndex = data.column.index;
            const rowIndex = data.row.index;

            // Only draw QR image for body cells, not header
            if (data.section === 'body' && headerTitles[columnIndex] === "QR") {
                const qrImage = qrImageMap.get(`${rowIndex}-${columnIndex}`);
                if (qrImage) {
                    // Optional: Center the QR image vertically in the taller cell
                    const imageSize = 20; // Adjust image size
                    const centerX = data.cell.x + (data.cell.width / 2) - (imageSize / 2);
                    const centerY = data.cell.y + (data.cell.height / 2) - (imageSize / 2);

                    doc.addImage(qrImage, 'PNG', centerX, centerY, imageSize, imageSize);
                }
            }
        },
    });

    doc.save(`${fileName}.pdf`);
};





